import type { GradientPreset, TextureOverlay } from '@/types/clock';

export interface GradientConfig {
  name: string;
  gradient: string;
  textColor: string;
  accentColor: string;
}

export const GRADIENT_PRESETS: Record<GradientPreset, GradientConfig> = {
  ocean: {
    name: 'Ocean Blue',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
    textColor: 'text-white',
    accentColor: '#667eea'
  },
  sunset: {
    name: 'Sunset',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    textColor: 'text-white',
    accentColor: '#fa709a'
  },
  'purple-dream': {
    name: 'Purple Dream',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    textColor: 'text-gray-800',
    accentColor: '#a8edea'
  },
  mint: {
    name: 'Mint Fresh',
    gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
    textColor: 'text-gray-800',
    accentColor: '#d299c2'
  },
  'rose-gold': {
    name: 'Rose Gold',
    gradient: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
    textColor: 'text-white',
    accentColor: '#e0c3fc'
  },
  carbon: {
    name: 'Dark Carbon',
    gradient: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    textColor: 'text-white',
    accentColor: '#2c5364'
  },
  aurora: {
    name: 'Aurora',
    gradient: 'linear-gradient(135deg, #13547a 0%, #80d0c7 100%)',
    textColor: 'text-white',
    accentColor: '#80d0c7'
  },
  cosmic: {
    name: 'Cosmic',
    gradient: 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
    textColor: 'text-white',
    accentColor: '#ff0844'
  },
  midnight: {
    name: 'Midnight',
    gradient: 'linear-gradient(135deg, #000428 0%, #004e92 100%)',
    textColor: 'text-white',
    accentColor: '#004e92'
  },
  pearl: {
    name: 'Pearl',
    gradient: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    textColor: 'text-gray-800',
    accentColor: '#c3cfe2'
  },
  custom: {
    name: 'Custom',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textColor: 'text-white',
    accentColor: '#667eea'
  }
};

export const TEXTURE_OVERLAYS: Record<TextureOverlay, string> = {
  none: '',
  'brushed-metal': `
    background-image: 
      repeating-linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.03) 0px,
        transparent 1px,
        transparent 2px,
        rgba(255, 255, 255, 0.03) 3px
      );
  `,
  'carbon-fiber': `
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    background-size: 20px 20px;
  `,
  marble: `
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  `,
  leather: `
    background-image: 
      radial-gradient(ellipse at center, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    background-size: 40px 40px;
  `,
  glass: `
    background-image: 
      linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(10px);
  `,
  wood: `
    background-image: 
      repeating-linear-gradient(
        0deg,
        rgba(139, 69, 19, 0.1) 0px,
        transparent 2px,
        transparent 4px,
        rgba(139, 69, 19, 0.1) 6px
      );
  `
};

export function getGradientStyle(preset: GradientPreset, customGradient?: { from: string; via?: string; to: string }): string {
  if (preset === 'custom' && customGradient) {
    return customGradient.via
      ? `linear-gradient(135deg, ${customGradient.from} 0%, ${customGradient.via} 50%, ${customGradient.to} 100%)`
      : `linear-gradient(135deg, ${customGradient.from} 0%, ${customGradient.to} 100%)`;
  }
  
  return GRADIENT_PRESETS[preset].gradient;
}

export function getTextureStyle(texture: TextureOverlay): string {
  return TEXTURE_OVERLAYS[texture];
}
